import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Vote, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { API_URL } from "../../lib/config";
import { ImageCarousel } from "../ImageCarousel";
import { MarkdownText } from "../MarkdownText";

export function VoteStep({
  consulta, isMultipleSelection, selectedOption, setSelectedOption,
  selectedOptions, toggleOption, calculateSelectedAmount,
  submitting, onSubmit, onBack
}) {
  const { t } = useTranslation();

  return (
    <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-slate-200 rounded-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><Vote className="h-6 w-6 text-blue-600" /></div>
        <div>
          <h3 className="text-xl font-heading font-semibold text-slate-900">{t('voting.vote')}</h3>
          <p className="text-sm text-slate-500">
            {isMultipleSelection
              ? consulta.restriction_type === 'options'
                ? t('voting.selectBetween', { min: consulta.min_options, max: consulta.max_options })
                : t('voting.selectUpToAmount', { amount: consulta.max_total_amount?.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' }) })
              : t('voting.selectYourOption')}
          </p>
        </div>
      </div>

      {isMultipleSelection && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          {consulta.restriction_type === 'options' ? (
            <p className="text-sm text-blue-800"><strong>{t('voting.selectedCount')}:</strong> {selectedOptions.length} {t('voting.ofMax')} {consulta.max_options} {t('voting.optionsLabel')}{consulta.min_options > 1 && ` (${t('voting.minimumLabel')} ${consulta.min_options})`}</p>
          ) : (
            <p className="text-sm text-blue-800"><strong>{t('voting.selectedAmount')}:</strong> {calculateSelectedAmount().toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })} {t('voting.ofMax')} {consulta.max_total_amount?.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })}</p>
          )}
        </div>
      )}

      {!isMultipleSelection && (
        <RadioGroup value={selectedOption} onValueChange={setSelectedOption} className="space-y-4 mb-6">
          {consulta.options.map((option, index) => {
            const uniqueValue = `${typeof option === 'string' ? option : option.title}:::${index}`;
            return <OptionCard key={index} option={option} index={index} isSelected={selectedOption === uniqueValue} onClick={() => setSelectedOption(uniqueValue)} radioValue={uniqueValue} radioMode />;
          })}
        </RadioGroup>
      )}

      {isMultipleSelection && (
        <div className="space-y-4 mb-6">
          {consulta.options.map((option, index) => {
            const title = typeof option === 'string' ? option : option.title;
            return <OptionCard key={index} option={option} index={index} isSelected={selectedOptions.includes(title)} onClick={() => toggleOption(title)} />;
          })}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack} className="flex-1">{t('voting.back')}</Button>
        <Button onClick={onSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700"
          disabled={(isMultipleSelection ? selectedOptions.length === 0 : !selectedOption) || submitting} data-testid="submit-vote-btn">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t('voting.confirmVote')}<CheckCircle2 className="h-5 w-5 ml-2" /></>}
        </Button>
      </div>
    </motion.div>
  );
}

function OptionCard({ option, index, isSelected, onClick, radioMode, radioValue }) {
  const optionTitle = typeof option === 'string' ? option : option.title;
  const optionDescription = typeof option === 'object' ? option.description : null;
  const optionImage = typeof option === 'object' ? option.image_url : null;
  const optionImages = typeof option === 'object' ? (option.image_urls?.length ? option.image_urls : (optionImage ? [optionImage] : [])) : [];
  const optionAmount = typeof option === 'object' ? option.amount : null;

  const carouselUrls = optionImages.map(u => `${API_URL}${u}`);

  return (
    <div className={`rounded-lg border-2 cursor-pointer transition-colors overflow-hidden ${isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
      onClick={onClick} data-testid={`vote-option-${index}`}>
      {carouselUrls.length > 0 && <ImageCarousel images={carouselUrls} alt={optionTitle} maxHeight="max-h-80" />}
      <div className="p-4 flex items-start space-x-3">
        {radioMode ? (
          <RadioGroupItem value={radioValue || optionTitle} id={`option-${index}`} className="mt-1" />
        ) : (
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-1 flex-shrink-0 ${isSelected ? "bg-blue-500 border-blue-500" : "border-slate-300"}`}>
            {isSelected && <CheckCircle2 className="h-3 w-3 text-white" />}
          </div>
        )}
        <div className="flex-1">
          {radioMode ? (
            <Label htmlFor={`option-${index}`} className="cursor-pointer font-medium text-slate-900">{optionTitle}</Label>
          ) : (
            <p className="cursor-pointer font-medium text-slate-900">{optionTitle}</p>
          )}
          {optionDescription && <MarkdownText className="text-sm text-slate-500 mt-1">{optionDescription}</MarkdownText>}
          {optionAmount && <p className="text-sm text-emerald-600 mt-1 font-medium">{optionAmount.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })}</p>}
        </div>
      </div>
    </div>
  );
}
